import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import $ from "jquery";
import ApiURl from "../controllers/Api";
import imgLocation from "../controllers/imagePath";

const StarRating = ({ rating, onRatingChange, readonly = false }) => {
  const [hover, setHover] = useState(0);

  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => !readonly && onRatingChange(star)}
          onMouseEnter={() => !readonly && setHover(star)}
          onMouseLeave={() => !readonly && setHover(0)}
          disabled={readonly}
          className={`text-2xl transition-colors ${
            readonly ? "cursor-default" : "cursor-pointer hover:scale-110"
          }`}
        >
          <span className={(hover || rating) >= star ? "text-yellow-400" : "text-gray-300"}>
            ★
          </span>
        </button>
      ))}
      <span className="ml-2 text-sm text-gray-600">
        {rating > 0 ? `${rating} out of 5` : "Click to rate"}
      </span>
    </div>
  );
};

const ReviewModal = ({
  isOpen,
  onClose,
  productId,
  existingReview,
  onSubmitSuccess,
  isAdmin = false
}) => {
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [adminName, setAdminName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const modalRef = useRef(null);
  const fileInputRef = useRef(null);

  const role = (localStorage.getItem("role") || "").toLowerCase();
  const computedIsAdmin = isAdmin || role === "admin";
  const userId = localStorage.getItem("user_id");

  // Helper function to construct image URL exactly like products
  const getImageUrl = (filename) => {
    if (!filename) return "";
    
    const cleanFilename = filename.replace(/^\/+/, "");
    const base = String(imgLocation || "").replace(/\/+$/, "");
    
    if (!base) return `/${cleanFilename}`;
    return `${base}/${cleanFilename}`;
  };

  // Extract filename from preview URL
  const getFilenameFromPreview = (previewUrl) => {
    if (previewUrl.startsWith('blob:')) return null;
    const parts = previewUrl.split('/');
    return parts[parts.length - 1];
  };

  // Parse images helper function
  const parseImages = (imageData) => {
    if (!imageData) return [];
    
    if (Array.isArray(imageData)) {
      return imageData;
    }
    
    if (typeof imageData === 'string') {
      if (imageData.startsWith('[') || imageData.startsWith('{')) {
        try {
          const parsed = JSON.parse(imageData);
          return Array.isArray(parsed) ? parsed : [parsed];
        } catch (e) {
          console.error("Error parsing images JSON:", e);
          if (imageData.includes(',')) {
            return imageData.split(',').map(f => f.trim()).filter(f => f);
          }
          return imageData ? [imageData] : [];
        }
      } else if (imageData.includes(',')) {
        return imageData.split(',').map(f => f.trim()).filter(f => f);
      } else {
        return imageData ? [imageData] : [];
      }
    }
    
    return [];
  };

  // Initialize form when modal opens
  useEffect(() => {
    if (!isOpen) return;

    if (existingReview) {
      console.log("Loading existing review:", existingReview);
      console.log("Review ID:", existingReview.id);
      
      setRating(Number(existingReview.rating || 0));
      setReviewText(existingReview.review_text || "");
      
      const parsedImages = parseImages(existingReview.images);
      console.log("Parsed images from review:", parsedImages);
      
      setExistingImages(parsedImages);
      
      const previewUrls = parsedImages.map(filename => getImageUrl(filename));
      setImagePreviews(previewUrls);
      setImageFiles([]);
      
      setAdminName(existingReview.admin_name || "");
    } else {
      resetForm();
    }

    setError("");
  }, [isOpen, existingReview]);

  const resetForm = () => {
    setRating(0);
    setReviewText("");
    
    imagePreviews.forEach(url => {
      if (url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    });
    
    setImageFiles([]);
    setImagePreviews([]);
    setExistingImages([]);
    setAdminName("");
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose]);

  // Clean up blob URLs when component unmounts
  useEffect(() => {
    return () => {
      imagePreviews.forEach(url => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, []);

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const totalImages = existingImages.length + imageFiles.length + files.length;
    if (totalImages > 5) {
      alert(`You can have a maximum of 5 photos total. You currently have ${existingImages.length + imageFiles.length} images.`);
      return;
    }

    setImageFiles(prev => [...prev, ...files]);
    
    const newPreviews = files.map((f) => URL.createObjectURL(f));
    setImagePreviews(prev => [...prev, ...newPreviews]);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeOneImage = (index) => {
    const previewToRemove = imagePreviews[index];
    
    if (previewToRemove.startsWith('blob:')) {
      // Remove new image
      setImageFiles(prev => {
        const newFiles = [...prev];
        // Calculate which file to remove based on position
        const newFilesCount = imageFiles.length;
        const existingCount = existingImages.length;
        const fileIndex = index - existingCount;
        if (fileIndex >= 0 && fileIndex < newFiles.length) {
          newFiles.splice(fileIndex, 1);
        }
        return newFiles;
      });
      
      URL.revokeObjectURL(previewToRemove);
    } else {
      // Remove existing image
      const filenameToRemove = getFilenameFromPreview(previewToRemove);
      if (filenameToRemove) {
        setExistingImages(prev => 
          prev.filter(filename => filename !== filenameToRemove)
        );
      }
    }

    // Update previews
    setImagePreviews(prev => {
      const newPreviews = [...prev];
      newPreviews.splice(index, 1);
      return newPreviews;
    });
  };

  const removeAllImages = () => {
    imagePreviews.forEach(url => {
      if (url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    });
    
    setImageFiles([]);
    setImagePreviews([]);
    setExistingImages([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async () => {
    if (rating === 0) return setError("Please select a rating");
    if (!reviewText.trim()) return setError("Please write a review");

    const isAdminReview =
      computedIsAdmin || (existingReview && Number(existingReview.is_admin_review) === 1);

    if (isAdminReview && !adminName.trim()) {
      return setError("Please enter admin name for the review");
    }

    console.log("=== SUBMITTING REVIEW ===");
    console.log("existingReview:", existingReview);
    console.log("Review ID:", existingReview?.id);
    console.log("existingImages to keep:", existingImages);
    console.log("imageFiles to upload:", imageFiles);

    setLoading(true);
    setIsSubmitting(true);
    setError("");

    try {
      const isEdit = !!existingReview;
      const formData = new FormData();
      
      // Always append these fields
      formData.append("product_id", productId);
      formData.append("user_id", userId || "");
      formData.append("rating", rating);
      formData.append("review_text", reviewText.trim());
      formData.append("is_admin_review", isAdminReview ? 1 : 0);
      
      if (isAdminReview && adminName.trim()) {
        formData.append("admin_name", adminName.trim());
      }

      // IMPORTANT: For edit, always append review_id
      if (isEdit) {
        if (!existingReview.id) {
          console.error("No review ID found in existingReview!");
          setError("Invalid review data: missing ID");
          setLoading(false);
          setIsSubmitting(false);
          return;
        }
        
        formData.append("review_id", existingReview.id);
        console.log("Appending review_id:", existingReview.id);
        
        formData.append("is_admin", computedIsAdmin ? 1 : 0);
        
        // Send existing images that we want to keep
        const existingImagesJson = JSON.stringify(existingImages);
        console.log("Appending existing_images:", existingImagesJson);
        formData.append("existing_images", existingImagesJson);
      }
      
      // Append new images
      imageFiles.forEach((file, index) => {
        console.log(`Appending file ${index}:`, file.name);
        formData.append("images[]", file);
      });

      const url = isEdit ? `${ApiURl}/updateReview.php` : `${ApiURl}/addReview.php`;
      console.log("Sending to URL:", url);

      // Log FormData contents (for debugging)
      for (let pair of formData.entries()) {
        console.log(pair[0] + ': ' + pair[1]);
      }

      const response = await $.ajax({
        url,
        method: "POST",
        data: formData,
        processData: false,
        contentType: false,
        dataType: "json"
      });

      console.log("Response from server:", response);

      if (response?.success) {
        onSubmitSuccess?.(response);
        onClose();
      } else {
        setError(response?.message || "Failed to submit review");
      }
    } catch (err) {
      console.error("Error submitting review:", err);
      setError(err?.responseJSON?.message || "An error occurred. Please try again.");
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const showAdminFields =
    computedIsAdmin || (existingReview && Number(existingReview.is_admin_review) === 1);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 p-4 overflow-y-auto"
      >
        <motion.div
          ref={modalRef}
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden"
        >
          <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-[#b29063]/10 to-white">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                {existingReview ? "Edit Review" : showAdminFields ? "Add Admin Review" : "Write a Review"}
              </h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
              >
                <svg className="h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="p-6 max-h-[70vh] overflow-y-auto">
            {showAdminFields && (
              <div className="mb-6">
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Admin Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={adminName}
                  onChange={(e) => setAdminName(e.target.value)}
                  placeholder="e.g., Store Manager"
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#b29063]/30 focus:border-[#b29063]"
                />
              </div>
            )}

            <div className="mb-6">
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Your Rating <span className="text-red-500">*</span>
              </label>
              <StarRating rating={rating} onRatingChange={setRating} />
            </div>

            <div className="mb-6">
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Your Review <span className="text-red-500">*</span>
              </label>
              <textarea
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                rows="5"
                placeholder={showAdminFields ? "Write an admin review..." : "Share your experience with this product..."}
                className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#b29063]/30 focus:border-[#b29063]"
              />
            </div>

            <div className="mb-6">
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Add Photos (Optional, max 5)
              </label>

              <label
                htmlFor="review-dropzone"
                className="flex flex-col items-center justify-center w-full rounded-2xl cursor-pointer border-2 border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100 transition p-8 text-center mb-3"
              >
                <div className="flex flex-col items-center justify-center gap-2">
                  <div className="h-12 w-12 rounded-2xl bg-white border border-gray-200 flex items-center justify-center shadow-sm">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="text-gray-600">
                      <path
                        d="M12 16V8M8 12h8"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                      <path
                        d="M20 16.5V18a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-1.5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                      <path
                        d="M7 10.5 12 6l5 4.5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-xs text-gray-500">
                    PNG, JPG, GIF (Max 5 files)
                  </p>
                </div>

                <input
                  ref={fileInputRef}
                  id="review-dropzone"
                  type="file"
                  className="hidden"
                  onChange={handleImageUpload}
                  accept="image/png, image/jpeg, image/gif"
                  multiple
                />
              </label>

              {imagePreviews.length > 0 && (
                <div className="mt-4">
                  <div className="rounded-2xl border border-gray-200 overflow-hidden bg-white shadow-sm p-3">
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                      {imagePreviews.map((src, idx) => (
                        <div key={idx} className="relative rounded-xl border border-gray-200 overflow-hidden bg-white group">
                          <img
                            src={src}
                            alt={`Preview ${idx + 1}`}
                            className="w-full h-28 object-contain bg-white"
                            onError={(e) => {
                              console.log("Image failed to load:", src);
                              e.target.onerror = null;
                              e.target.src = "data:image/svg+xml;utf8," + encodeURIComponent(`
                                <svg xmlns="http://www.w3.org/2000/svg" width="100" height="100">
                                  <rect width="100%" height="100%" fill="#f2f2f2"/>
                                  <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle"
                                    font-family="Arial" font-size="12" fill="#777">
                                    Failed to load
                                  </text>
                                </svg>
                              `);
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => removeOneImage(idx)}
                            className="absolute top-2 right-2 rounded-full px-2 py-1 text-xs bg-white/90 border border-gray-200 hover:bg-gray-50 transition opacity-0 group-hover:opacity-100"
                          >
                            ✕
                          </button>
                          {!src.startsWith('blob:') && (
                            <span className="absolute bottom-2 left-2 text-xs bg-black/50 text-white px-2 py-0.5 rounded">
                              Existing
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-3 flex items-center justify-between text-xs text-gray-600">
                    <span>
                      {existingImages.length} existing + {imageFiles.length} new = {imagePreviews.length} total
                    </span>
                    <button
                      type="button"
                      onClick={removeAllImages}
                      className="rounded-full px-3 py-1 border border-gray-200 hover:bg-gray-50 transition"
                    >
                      Remove All
                    </button>
                  </div>
                </div>
              )}
            </div>

            {error && (
              <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex justify-end gap-3">
              <button
                onClick={onClose}
                className="px-6 py-2.5 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
                type="button"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading || isSubmitting}
                className="px-6 py-2.5 bg-gradient-to-r from-[#b29063] to-[#9a7a52] text-white rounded-xl text-sm font-medium hover:shadow-lg disabled:opacity-50 transition-all cursor-pointer"
                type="button"
              >
                {loading ? "Submitting..." : existingReview ? "Update Review" : "Submit Review"}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ReviewModal;